import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User as UserIcon, Mail, Building2, Hash, LogOut, ShieldCheck,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * My Account page — reached from the TopBar avatar menu.
 *
 * Shows the signed-in customer's name, business email, status and
 * customer id (the IDs that support sometimes asks for). Provides a
 * sign-out action. Read-only by design — the onboarding /business-profile
 * page handles edits during setup and is separately protected by an
 * onboarding_email session guard.
 */
export default function AccountPage() {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("current_customer")
                || localStorage.getItem("current_customer");
      if (raw) setCustomer(JSON.parse(raw));
    } catch {
      /* ignore parse errors — page will show 'Not signed in' */
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSignOut = async () => {
    // Soft sign-out — clear local session and bounce to login.
    // Server-side session is stateless (sessionStorage-based) so this
    // is enough; if a JWT/cookie flow lands later, hook the server
    // /api/auth/logout call in here.
    try {
      await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    } catch { /* offline / unauthenticated — proceed anyway */ }
    sessionStorage.removeItem("current_customer");
    localStorage.removeItem("current_customer");
    navigate("/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="px-6 py-10 text-[13px] text-slate-500"
           data-testid="account-loading">Loading…</div>
    );
  }

  if (!customer) {
    return (
      <div className="px-6 py-10 max-w-2xl" data-testid="account-empty">
        <h1 className="text-2xl font-semibold text-slate-900 mb-2">My Account</h1>
        <p className="text-[13px] text-slate-600 mb-4">
          No active session. Please sign in to view your account.
        </p>
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-[13px] font-medium text-white hover:bg-emerald-700"
        >
          Go to Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 tablet:px-5 desktop:px-6 py-6 desktop:py-8 max-w-3xl mx-auto" data-testid="account-page">
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">My Account</h1>
      <p className="text-[13px] text-slate-600 mb-6">
        Signed-in session details. To edit your business profile, use the
        Business Profile flow during onboarding setup.
      </p>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <Field testId="account-name"     icon={UserIcon}    label="Account holder"  value={customer.name || "—"} />
        <Field testId="account-email"    icon={Mail}        label="Business email"  value={customer.email || "—"} />
        <Field testId="account-company"  icon={Building2}   label="Company"         value={customer.company_name || customer.name || "—"} />
        <Field testId="account-status"   icon={ShieldCheck} label="Status"          value={prettifyStatus(customer.status)} />
        <Field testId="account-id"       icon={Hash}        label="Customer ID"     value={customer.id || "—"} mono />
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button
          type="button"
          data-testid="account-signout"
          onClick={handleSignOut}
          className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-white px-4 py-2 text-[13px] font-medium text-rose-700 hover:bg-rose-50"
        >
          <LogOut size={14} /> Sign out
        </button>
        <button
          type="button"
          data-testid="account-back"
          onClick={() => navigate("/executive")}
          className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-[13px] font-medium text-slate-700 hover:border-slate-300"
        >
          Back to Boardroom
        </button>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, mono, testId }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b last:border-b-0 border-slate-100">
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-slate-50">
        <Icon size={13} className="text-slate-500" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        <div data-testid={testId}
             className={`text-[13.5px] text-slate-900 truncate ${mono ? "font-mono" : ""}`}>
          {value}
        </div>
      </div>
    </div>
  );
}

function prettifyStatus(s) {
  if (!s) return "—";
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");
}
